import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/use-auth';
import { ChangePasswordSchema } from '@/lib/schemas/auth';
import { ApiError } from '@/lib/api';
import { PASSWORD_MIN_LENGTH } from '@/lib/password-policy';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

function errorMessage(code: string): string {
  switch (code) {
    case 'INVALID_CURRENT_PASSWORD':
      return 'Current password is incorrect.';
    case 'USER_NOT_FOUND':
      return 'Your session is no longer valid. Please sign in again.';
    default:
      return 'Could not change password. Please try again.';
  }
}

export function ProfileChangePasswordPage() {
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();

  const isTutor = user?.role === 'tutor';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPolicyError(null);

    if (newPassword !== confirmPassword) {
      setPolicyError('New password and confirmation do not match.');
      return;
    }

    const parsed = ChangePasswordSchema.safeParse({ currentPassword, newPassword });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setPolicyError(first?.message ?? 'Invalid input.');
      return;
    }

    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/profile', { replace: true }), 1500);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          navigate('/login', { replace: true });
        } else {
          setError(errorMessage(err.errorCode));
        }
      } else {
        setError('Network error. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl py-2">
      {!isTutor && <h1 className="text-2xl font-semibold mb-6">Change Password</h1>}

      <Card>
        <CardHeader>
          <CardTitle className={isTutor ? 'text-lg font-semibold' : undefined}>Update Your Password</CardTitle>
          <CardDescription>
            Enter your current password and choose a new one
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {policyError && (
            <Alert variant="warning" className="mb-4">
              <AlertDescription>{policyError}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              <AlertTitle>Password Updated</AlertTitle>
              <AlertDescription>
                Your password has been changed successfully. A confirmation email has been sent to your registered email address. You have been logged out on all devices. Redirecting to profile…
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="current">
                Current Password {error && <span className="text-red-500">*</span>}
              </Label>
              <PasswordInput
                id="current"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                disabled={busy || success}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new">
                New Password {error && <span className="text-red-500">*</span>}
                <span className="text-gray-500 text-sm ml-2">
                  (≥ {PASSWORD_MIN_LENGTH} chars, must be different)
                </span>
              </Label>
              <PasswordInput
                id="new"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                disabled={busy || success}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">
                Confirm New Password {error && <span className="text-red-500">*</span>}
              </Label>
              <PasswordInput
                id="confirm"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                disabled={busy || success}
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={busy || success} className="flex-1">
                {busy ? 'Updating…' : 'Change Password'}
              </Button>
              {!isTutor && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/profile')}
                  disabled={busy || success}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
